import { useEffect, useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { Reveal } from "./Reveal";
import { SERVICE_OPTIONS, CLINIC } from "../lib/content";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { CalendarIcon, Check, Loader2 } from "lucide-react";
import { trackLeadSubmission } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TIMES = ["10:00 AM", "11:00 AM", "12:00 PM", "02:00 PM", "03:30 PM", "05:00 PM", "06:00 PM"];

export default function Booking() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", time: "", message: "" });
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const onBookWith = (e) => {
      const cond = e.detail;
      if (!cond) return;
      setForm((f) => ({
        ...f,
        service: f.service || "General Consultation",
        message: f.message ? `${f.message}\nInterested in: ${cond}` : `I'd like to consult about: ${cond}`,
      }));
    };
    window.addEventListener("book-with", onBookWith);
    return () => window.removeEventListener("book-with", onBookWith);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.service || !date || !form.time) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/appointments`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        service: form.service,
        preferred_date: format(date, "yyyy-MM-dd"),
        preferred_time: form.time,
        message: form.message,
      });
      trackLeadSubmission();
      setDone(true);
      toast.success("Request received! Your confirmation email is on its way.");
      setForm({ name: "", email: "", phone: "", service: "", time: "", message: "" });
      setDate(null);
    } catch (err) {
      toast.error("Something went wrong. Please call the clinic directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="booking" className="py-24 md:py-32 bg-secondary/25">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-12 lg:gap-16 items-start">
          <Reveal>
            <span className="overline text-accent">Book an appointment</span>
            <h2 className="font-serif text-primary text-4xl sm:text-5xl tracking-tight leading-[1.02] mt-5">
              Reserve your visit in a minute.
            </h2>
            <p className="mt-6 text-muted-foreground leading-relaxed">
              Share a few details and pick a preferred time. Our team will call you back to confirm the exact slot with {CLINIC.doctor}.
            </p>
            <div className="mt-8 space-y-3 text-sm">
              {["Free 5-minute callback confirmation", "Private, unhurried consultations", "Same-week slots often available"].map((t) => (
                <div key={t} className="flex items-center gap-3 text-foreground">
                  <span className="w-6 h-6 rounded-full bg-accent/20 text-accent grid place-items-center"><Check size={14} /></span>
                  {t}
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <form
              onSubmit={submit}
              data-testid="booking-form"
              className="bg-card border border-border rounded-[1.5rem] p-6 sm:p-9"
            >
              {done && (
                <div className="mb-6 rounded-xl bg-accent/15 border border-accent/30 p-4 text-sm text-foreground flex items-center gap-3" data-testid="booking-success">
                  <Check className="text-accent" size={18} /> Your request was sent, and a confirmation email is on its way.
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="name">Full name *</Label>
                  <Input id="name" data-testid="booking-name" value={form.name} onChange={set("name")} placeholder="Your name" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone *</Label>
                  <Input id="phone" data-testid="booking-phone" value={form.phone} onChange={set("phone")} placeholder="+91 …" />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input id="email" type="email" data-testid="booking-email" value={form.email} onChange={set("email")} placeholder="you@email.com" />
                </div>

                <div className="space-y-2">
                  <Label>Service *</Label>
                  <Select value={form.service} onValueChange={set("service")}>
                    <SelectTrigger data-testid="booking-service"><SelectValue placeholder="Select care" /></SelectTrigger>
                    <SelectContent>
                      {SERVICE_OPTIONS.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Preferred time *</Label>
                  <Select value={form.time} onValueChange={set("time")}>
                    <SelectTrigger data-testid="booking-time"><SelectValue placeholder="Select time" /></SelectTrigger>
                    <SelectContent>
                      {TIMES.map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2 sm:col-span-2">
                  <Label>Preferred date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        data-testid="booking-date"
                        className="w-full justify-start font-normal border-input"
                      >
                        <CalendarIcon size={16} className="mr-2 text-muted-foreground" />
                        {date ? format(date, "PPP") : <span className="text-muted-foreground">Pick a date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="message">Anything we should know?</Label>
                  <Textarea id="message" data-testid="booking-message" value={form.message} onChange={set("message")} placeholder="Optional notes…" rows={3} />
                </div>
              </div>

              <Button
                type="submit"
                data-testid="booking-submit"
                disabled={loading}
                className="w-full mt-7 rounded-full py-6 text-sm font-bold bg-accent text-accent-foreground hover:bg-accent/90 hover:-translate-y-0.5 transition-transform duration-300"
              >
                {loading ? (<><Loader2 size={16} className="mr-2 animate-spin" /> Sending…</>) : "Request appointment"}
              </Button>
              <p className="text-xs text-muted-foreground text-center mt-4">
                By submitting, you agree to be contacted by the clinic and receive a confirmation email.
              </p>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
